import { useState } from 'react';
import { Smile } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { EMOJI_CATEGORIES, searchEmoji } from '../../lib/publishing/emojiData';

// Publishing composer §B — the caption toolbar's emoji picker.
//
// HAND-ROLLED, NO NEW DEPENDENCY. A full emoji-picker package pulls in its own
// data file, styling and popover; all this needs is "drop the right glyph into a
// caption". The dataset is a much larger BAKED list now (src/lib/publishing/
// emojiData.ts — the eight standard categories), browsed via category tabs and
// searched across all of them, with a render cap so the popover stays cheap. Same
// posture as the hand-rolled Sidebar / date-lib-free DateRangePicker.
//
// Caret insertion lives in CaptionBox (this only calls onPick with the glyph).

// Cap what's painted at once. Categories are well under this; it bounds SEARCH,
// where a broad query ("a") could otherwise match a lot of the flat set.
const RENDER_CAP = 180;

export function EmojiPicker({
  onPick,
  disabled,
}: {
  onPick: (emoji: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState(EMOJI_CATEGORIES[0]!.id);

  const q = query.trim().toLowerCase();
  const searching = q !== '';
  const category = EMOJI_CATEGORIES.find((c) => c.id === activeCat) ?? EMOJI_CATEGORIES[0]!;

  const search = searching ? searchEmoji(q, RENDER_CAP) : null;
  const shown = searching ? search!.emoji : category.emoji.slice(0, RENDER_CAP);
  const truncated = searching ? search!.truncated : category.emoji.length > RENDER_CAP;

  const pick = (char: string) => {
    onPick(char);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          aria-label="Insert emoji"
          title="Insert emoji"
          className="h-7 px-2 text-slate-500 hover:text-slate-700"
        >
          <Smile className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <Input
          value={query}
          autoFocus
          placeholder="Search emoji…"
          className="h-8 text-sm"
          onChange={(e) => setQuery(e.target.value)}
        />

        {/* Category tabs — hidden while searching (search spans all categories). */}
        {!searching && (
          <div className="mt-2 flex items-center justify-between gap-0.5">
            {EMOJI_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                title={c.name}
                aria-label={c.name}
                aria-pressed={c.id === activeCat}
                onClick={() => setActiveCat(c.id)}
                className={cn(
                  'flex-1 rounded p-1 text-center text-base leading-none hover:bg-slate-100',
                  c.id === activeCat && 'bg-slate-100 ring-1 ring-slate-200',
                )}
              >
                {c.icon}
              </button>
            ))}
          </div>
        )}

        <div className="scroll-subtle mt-2 max-h-64 overflow-y-auto pr-1">
          <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {searching ? 'Results' : category.name}
          </p>
          {shown.length === 0 ? (
            <p className="px-1 py-4 text-center text-xs text-slate-400">No matches.</p>
          ) : (
            <div className="grid grid-cols-8 gap-0.5">
              {shown.map((e) => (
                <button
                  key={e.char}
                  type="button"
                  title={e.keywords.split(' ')[0]}
                  onClick={() => pick(e.char)}
                  className="rounded p-1 text-lg leading-none hover:bg-slate-100"
                >
                  {e.char}
                </button>
              ))}
            </div>
          )}
          {truncated && (
            <p className="mt-1 px-1 text-center text-[10px] text-slate-400">
              Keep typing to narrow the results.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
